import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useT } from '../i18n';
import { useRole } from '../context/RoleContext';
import { api } from '../api/client';
import { SearchableSelect } from './SearchableSelect';
import { DropdownSelect } from './DropdownSelect';
import { getMilestoneOptions } from './ProjectForm';
import { MissingMilestoneNotice, taskSaveErrorMessage } from './TaskMilestoneNotice';
import { getDoneStatusIds, getInboxStatusIds } from '../utils/stages';
import { projectDisplayCode } from '../utils/identifiers';
import type { GridColumn, Project } from '../types';

// "Migrer vers un autre projet": moves a task to another primary project. It is
// offered from every place a task is edited (task page, task overlay, the
// Execution tab's milestones/tasks view). The target is any project that can
// hold tasks — same rules as task creation: not in the Inbox or a closed
// stage, and members only see their own projects. The milestone in the target
// is optional (and there is none to pick while the target has none yet): a
// task without one says so wherever it is shown (see TaskMilestoneNotice).
// Rendered in a portal so it can be opened
// from inside another modal.
export function MigrateTaskModal({ taskId, fromProjectId, projects, onClose, onMigrated }: {
  taskId: number;
  /** The task's current (home) project — excluded from the targets. */
  fromProjectId: number;
  /** Candidate projects; filtered here to the ones that may hold tasks. */
  projects: Project[];
  onClose: () => void;
  /** Called once the task has moved — the caller reloads or closes its own view. */
  onMigrated: () => void;
}) {
  const { t } = useT();
  const { canSeeAllProjects, projectIds } = useRole();
  const [stages, setStages] = useState<GridColumn[]>([]);
  const [targetId, setTargetId] = useState<number>(0);
  const [milestoneId, setMilestoneId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (api.getGridColumns('pipeline') as Promise<GridColumn[]>).then(setStages).catch(() => setStages([]));
  }, []);

  const targets = useMemo(() => {
    const inbox = getInboxStatusIds(stages);
    const done = getDoneStatusIds(stages);
    return projects.filter(p =>
      p.id !== fromProjectId
      && !inbox.has(p.status_id) && !done.has(p.status_id)
      && (canSeeAllProjects || projectIds.includes(p.id)));
  }, [projects, stages, fromProjectId, canSeeAllProjects, projectIds]);
  const targetOptions = useMemo(
    () => targets.map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` })),
    [targets]);
  const target = targets.find(p => p.id === targetId);
  const milestoneOptions = useMemo(
    () => target ? getMilestoneOptions(target.planning, t('common.dateFormatPlaceholder'), t('taskDetail.milestoneDuePrefix')) : [],
    [target, t]);
  const targetHasMilestones = milestoneOptions.length > 0;
  const ready = !!target && !busy;

  const confirm = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await api.migrateTask(taskId, targetId, milestoneId || null);
      onMigrated();
    } catch (err: any) {
      alert(taskSaveErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 className="modal-title">{t('tasks.migrateTask')}</h3>
          <button className="close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">{t('tasks.migrateTargetLabel')}</label>
            {targetOptions.length > 0 ? (
              <SearchableSelect
                value={targetId}
                options={targetOptions}
                placeholder={t('tasks.selectProjectPlaceholder')}
                onChange={(v) => { setTargetId(Number(v)); setMilestoneId(''); }}
              />
            ) : (
              <p style={{ margin: 0, fontSize: 'var(--t-12)', color: 'var(--ink-3)', lineHeight: 1.5 }}>{t('tasks.migrateNoTargets')}</p>
            )}
          </div>
          {target && (
            <div className="form-group">
              <label className="form-label">{t('tasks.migrateMilestoneLabel')}</label>
              {targetHasMilestones ? (
                <>
                  <DropdownSelect value={milestoneId} options={milestoneOptions} placeholder={t('tasks.chooseMilestone')}
                    onChange={(v) => setMilestoneId(String(v))} />
                  {!milestoneId && <MissingMilestoneNotice />}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: 'var(--t-12)', color: 'var(--ink-3)', lineHeight: 1.5 }}>{t('tasks.migrateNoMilestoneHint')}</p>
              )}
            </div>
          )}
          <p style={{ margin: 0, fontSize: 'var(--t-12)', color: 'var(--ink-3)', lineHeight: 1.5 }}>
            {t('tasks.migrateWarning')}
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-accent" disabled={!ready} onClick={confirm}>
            {t('tasks.migrateConfirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Who may move a task to another project: the PMO (admins) and the PMO
 * accompanist of the task's project (the server checks it too).
 */
export function canMigrateTask(isAdmin: boolean, userName: string | null | undefined, projectOwnership: unknown): boolean {
  if (isAdmin) return true;
  const own = typeof projectOwnership === 'string' ? (() => { try { return JSON.parse(projectOwnership); } catch { return null; } })() : projectOwnership;
  const accompanist = String((own as { pmo_accompanist?: string } | null)?.pmo_accompanist ?? '').trim().toLowerCase();
  return !!accompanist && !!userName && userName.trim().toLowerCase() === accompanist;
}
